import { useEffect, useRef, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { KeyRound, Building2, ShieldCheck, ScrollText, Download, Plus, Trash2, Copy, Check } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import { DateField } from '../../components/DateField';
import { NotificationsToggle } from '../../components/NotificationsToggle';
import { formatPhone, PHONE_MASK } from '../../lib/format';
import { OWNER_ADDRESS, OWNER_EMAIL, OWNER_FIRST } from '../../lib/identity';

const DEFAULT_BIZ = {
  name: 'AJM Aviation Ventures, LLC',
  address: OWNER_ADDRESS,
  email: OWNER_EMAIL,
  phone: '',
};

export function AdminSettings() {
  const { demoMode } = useAuth();
  const [pin, setPin] = useState('AJM2026');
  const [pinDraft, setPinDraft] = useState(pin);
  const [biz, setBiz] = useState(DEFAULT_BIZ);
  const [savingBiz, setSavingBiz] = useState(false);
  // Snapshot of the last-saved business info → drives the Save/Saved button state so
  // the user always knows whether their current edits are persisted.
  const savedBiz = useRef(JSON.stringify(DEFAULT_BIZ));
  const bizDirty = JSON.stringify(biz) !== savedBiz.current;

  // Load the real saved business info (config/global.business) so edits persist.
  useEffect(() => {
    if (demoMode || !db) return;
    const unsub = onSnapshot(doc(db, 'config', 'global'), (snap) => {
      const b = snap.data()?.business;
      if (b) {
        const merged = { ...DEFAULT_BIZ, ...b };
        savedBiz.current = JSON.stringify(merged);
        setBiz(merged);
      }
    });
    return () => unsub();
  }, [demoMode]);

  async function saveBiz() {
    if (demoMode || !db) {
      savedBiz.current = JSON.stringify(biz);
      flash('Business info saved (demo).');
      return;
    }
    setSavingBiz(true);
    try {
      await setDoc(doc(db, 'config', 'global'), { business: biz }, { merge: true });
      savedBiz.current = JSON.stringify(biz);
      flash('Business info saved.');
    } catch {
      flash('Could not save — try again.');
    } finally {
      setSavingBiz(false);
    }
  }
  const [msg, setMsg] = useState<string | null>(null);

  function flash(text: string) {
    setMsg(text);
    setTimeout(() => setMsg(null), 2500);
  }

  return (
    <>
      <PageHeader title="Settings" subtitle="Configuration, announcements, and audit log." />
      {msg && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2 text-sm mb-4">
          {msg}
        </div>
      )}

      <div className="space-y-6">
        <NotificationsToggle />

        <Section icon={<KeyRound size={16} />} title="Registration PIN" desc="Required to start lessee registration. Share via SMS — never email.">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <label className="label">PIN</label>
              <input
                className="input tracking-widest font-mono"
                value={pinDraft}
                onChange={(e) => setPinDraft(e.target.value)}
              />
              <div className="text-xs text-ink-muted mt-1 flex items-center gap-1.5">
                Current: <code>{pin}</code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(pin);
                    flash('PIN copied — paste it into a text.');
                  }}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-navy-700 hover:bg-slate-100"
                  title="Copy PIN"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
            </div>
            <button
              onClick={() => {
                setPin(pinDraft);
                flash(demoMode ? 'PIN updated (demo).' : 'PIN updated.');
              }}
              className="btn-primary"
              disabled={pinDraft === pin || !pinDraft.trim()}
            >
              Update PIN
            </button>
          </div>
        </Section>

        <Section icon={<Building2 size={16} />} title="Business info" desc="Shown on statements, lease, and email signatures.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Legal name" value={biz.name} onChange={(v) => setBiz({ ...biz, name: v })} />
            <Field label="Contact email" value={biz.email} onChange={(v) => setBiz({ ...biz, email: v })} />
            <Field label="Contact phone" placeholder={PHONE_MASK} value={biz.phone} onChange={(v) => setBiz({ ...biz, phone: formatPhone(v) })} />
            <Field label="Address" className="sm:col-span-2" value={biz.address} onChange={(v) => setBiz({ ...biz, address: v })} />
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={saveBiz}
              disabled={savingBiz || !bizDirty}
              className={bizDirty ? 'btn-primary' : 'btn-secondary'}
            >
              {savingBiz ? 'Saving…' : bizDirty ? 'Save' : (
                <span className="flex items-center gap-1.5">
                  <Check size={15} /> Saved
                </span>
              )}
            </button>
          </div>
        </Section>

        <Section icon={<ShieldCheck size={16} />} title="Two-factor authentication" desc="Required for admin accounts. Setup via Firebase Authentication.">
          <div className="rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 text-sm text-navy-900">
            2FA setup screen ships when Firebase is wired. The flow uses Firebase Auth’s
            built-in multi-factor support (phone-based by default). Lessees see an optional setup
            prompt after first login.
          </div>
        </Section>

        <Section icon={<ScrollText size={16} />} title="Audit log" desc="Append-only record of every status change, document verification, override, and admin action.">
          <table className="w-full text-sm">
            <thead className="text-left text-ink-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="py-2 font-medium">When</th>
                <th className="py-2 font-medium">Actor</th>
                <th className="py-2 font-medium">Action</th>
                <th className="py-2 font-medium">Entity</th>
              </tr>
            </thead>
            <tbody className="text-ink">
              <AuditRow when="Today 11:14 AM" actor={`${OWNER_FIRST} (admin)`} action="Verified document" entity="d-004 (Morgan medical)" />
              <AuditRow when="Today 11:13 AM" actor={`${OWNER_FIRST} (admin)`} action="Approved booking" entity="b-001 (Brian)" />
              <AuditRow when="Yesterday 6:02 PM" actor="System" action="Stripe charge succeeded" entity="ch_demo_001" />
              <AuditRow when="Yesterday 5:58 PM" actor="Brian Matthews" action="Logged flight" entity="f-001" />
              <AuditRow when="2 days ago" actor={`${OWNER_FIRST} (admin)`} action="Approved registration" entity="Jordan Lee" />
            </tbody>
          </table>
          <p className="text-xs text-ink-muted mt-3">Full filterable audit log ships when Firestore audit-write Cloud Function is wired.</p>
        </Section>

        <Section icon={<Download size={16} />} title="Database backup" desc="Download a JSON snapshot of every collection.">
          <button
            onClick={() => {
              const blob = new Blob([JSON.stringify({ exported: 'demo', notice: 'Live backups require Firebase admin SDK' }, null, 2)], {
                type: 'application/json',
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `ajm-backup-${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="btn-secondary"
          >
            Download backup (demo stub)
          </button>
        </Section>
      </div>
    </>
  );
}

function Section({
  icon,
  title,
  desc,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  desc?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="mt-0.5 text-navy-700">{icon}</div>
        <div>
          <h2 className="font-display text-lg font-semibold text-navy-900">{title}</h2>
          {desc && <p className="text-sm text-ink-muted mt-0.5">{desc}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  className = '',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
  placeholder?: string;
}) {
  return (
    <div className={className}>
      <label className="label">{label}</label>
      <input className="input" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function AuditRow({ when, actor, action, entity }: { when: string; actor: string; action: string; entity: string }) {
  return (
    <tr className="border-b border-slate-100 last:border-none">
      <td className="py-2 whitespace-nowrap text-ink-muted">{when}</td>
      <td className="py-2">{actor}</td>
      <td className="py-2">{action}</td>
      <td className="py-2 font-mono text-xs">{entity}</td>
    </tr>
  );
}
